import type { JSX } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatDateBR, formatDateOnly } from "@/utils/dateUtils";
import { AlocacaoChip } from "@/components/freelancers/AlocacaoChip";
import {
  getDisponibilidadeConfig,
  axisValueLabel,
  fonteLabel,
  frescorLabel,
  frescorOpacity,
  temperaturaMotivo,
  EIXO_LABEL,
  TEMPERATURA_CONFIG,
  type DisponibilidadeSummary,
} from "@/utils/disponibilidadeUtils";

// Pontinho de temperatura junto do badge (por freelancer). Sem info não marca nada.
const TEMPERATURA_DOT: Record<string, string> = {
  quente: "bg-orange-500",
  morno: "bg-amber-400",
  frio: "bg-sky-400",
};

type BadgeLevel = "dot" | "inline" | "chip";

interface DisponibilidadeBadgeProps {
  /** Linha de v_availability_summary. Ausente = freelancer sem sinal -> "Sem info". */
  summary?: DisponibilidadeSummary | null;
  level?: BadgeLevel;
  showTooltip?: boolean;
  className?: string;
}

const DIMENSION_ORDER = ["situacao_emprego", "interesse_freela", "relacao_conosco"];

function Composition({ summary }: { summary?: DisponibilidadeSummary | null }) {
  const config = getDisponibilidadeConfig(summary?.disponibilidade_efetiva);
  const axes = summary?.axes ?? {};
  return (
    <div className="space-y-1.5 text-xs max-w-[260px]">
      <p className="font-medium flex items-center gap-1">
        <config.icon className={cn("h-3.5 w-3.5", config.color)} />
        {config.text}
      </p>
      <div className="space-y-0.5 text-muted-foreground">
        {DIMENSION_ORDER.map((dimension) => {
          const axis = axes[dimension];
          const valueLabel = axisValueLabel(dimension, axis?.value);
          const sourceLabel = fonteLabel(axis?.source);
          return (
            <p key={dimension}>
              <span className="text-foreground/80">{EIXO_LABEL[dimension]}:</span> {valueLabel}
              {sourceLabel ? ` (${sourceLabel}${axis?.confidence === "baixa" ? ", a confirmar" : ""})` : ""}
            </p>
          );
        })}
      </div>
      {typeof summary?.carga_atual === "number" && summary.carga_atual > 0 && (
        <p className="text-muted-foreground">
          Ocupação: {summary.carga_atual} projeto{summary.carga_atual > 1 ? "s" : ""} agora
          {summary.headroom && summary.headroom > 0 ? ", tem espaço pra mais" : ""}
          {summary.capacity_is_floor ? " (mínimo observado)" : ""}
        </p>
      )}
      {typeof summary?.carga_atual === "number" && summary.carga_atual > 0 && (
        summary.tem_projeto_fixo ? (
          <p className="text-muted-foreground">
            Projeto fixo, sem data de término
            {summary.next_freeing_date
              ? `; outro renova em ${formatDateBR(summary.next_freeing_date)}`
              : ""}
          </p>
        ) : summary.next_freeing_date ? (
          <p className="text-muted-foreground">
            Renova em {formatDateBR(summary.next_freeing_date)}
          </p>
        ) : null
      )}
      {summary?.conflito_emprego_interesse && (
        <p className="text-amber-600 flex items-start gap-1">
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
          Empregada, mas sinalizou que topa freela. Vale abordar.
        </p>
      )}
      {summary?.frescor_interesse && frescorLabel(summary.frescor_interesse) && (
        <p className="text-muted-foreground/80 italic">
          {frescorLabel(summary.frescor_interesse)}
        </p>
      )}
      {summary?.temperatura && summary.temperatura !== "sem_info" && (
        <p className="flex items-start gap-1 pt-0.5 border-t border-border/50">
          <span
            className={cn("mt-1 h-2 w-2 rounded-full shrink-0", TEMPERATURA_DOT[summary.temperatura])}
          />
          <span>
            <span className="font-medium">{TEMPERATURA_CONFIG[summary.temperatura].text}.</span>{" "}
            <span className="text-muted-foreground">{temperaturaMotivo(summary)}</span>
          </span>
        </p>
      )}
      {summary && (
        <div className="text-muted-foreground/80 space-y-0.5">
          {summary.ultimo_sinal && <p>Status desde {formatDateOnly(summary.ultimo_sinal)}</p>}
          <p>
            {summary.ultima_consulta
              ? `Última consulta: ${formatDateOnly(summary.ultima_consulta)}`
              : "Nunca consultado diretamente"}
          </p>
          {summary.reconsulta_vencida && summary.reconsulta_due && (
            <p className="text-amber-600">
              Reconsulta atrasada desde {formatDateOnly(summary.reconsulta_due)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function DisponibilidadeBadge({
  summary,
  level = "chip",
  showTooltip = true,
  className,
}: DisponibilidadeBadgeProps) {
  const config = getDisponibilidadeConfig(summary?.disponibilidade_efetiva);
  const Icon = config.icon;
  const fadeClass = frescorOpacity(summary?.frescor_interesse);
  const hasConflict = !!summary?.conflito_emprego_interesse;
  const temperaturaDot =
    summary?.temperatura && summary.temperatura !== "sem_info"
      ? TEMPERATURA_DOT[summary.temperatura]
      : null;

  let badgeContent: JSX.Element;
  if (level === "dot") {
    badgeContent = (
      <span
        className={cn("inline-flex items-center", fadeClass, className)}
        aria-label={config.text}
      >
        <Icon className={cn("h-3.5 w-3.5", config.color)} />
      </span>
    );
  } else if (level === "inline") {
    badgeContent = (
      <span
        className={cn("inline-flex items-center gap-1 text-xs", config.color, fadeClass, className)}
        aria-label={config.text}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{config.text}</span>
        {temperaturaDot && <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", temperaturaDot)} />}
        {hasConflict && <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />}
      </span>
    );
  } else {
    badgeContent = (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
          config.bg,
          config.color,
          config.border,
          fadeClass,
          className
        )}
        aria-label={config.text}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{config.text}</span>
        {temperaturaDot && <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", temperaturaDot)} />}
        {hasConflict && <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />}
      </span>
    );
  }

  if (!showTooltip) return badgeContent;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default">{badgeContent}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="p-2">
          <Composition summary={summary} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Data do status em texto discreto, pra ficar ao lado do badge nos modos wide/lista. */
export function DisponibilidadeDataHint({
  summary,
  className,
}: {
  summary?: DisponibilidadeSummary | null;
  className?: string;
}) {
  if (!summary?.ultimo_sinal) return null;
  return (
    <span className={cn("text-[10px] text-muted-foreground whitespace-nowrap", className)}>
      desde {formatDateOnly(summary.ultimo_sinal, "dd/MM/yy")}
    </span>
  );
}

/**
 * Linha completa de disponibilidade (badge + "Até dd/mm" + "desde dd/MM/yy"),
 * o mesmo desenho da listagem de freelancers. Preferir este composto em vez do
 * badge sozinho pra data do status aparecer em toda tela.
 */
export function DisponibilidadeLinha({
  summary,
  level = "inline",
  className,
}: {
  summary?: DisponibilidadeSummary | null;
  level?: BadgeLevel;
  className?: string;
}) {
  return (
    <span className={cn("inline-flex flex-wrap items-center gap-1.5 min-w-0", className)}>
      <DisponibilidadeBadge summary={summary} level={level} className="min-w-0" />
      <AlocacaoChip summary={summary} />
      <DisponibilidadeDataHint summary={summary} />
    </span>
  );
}

export default DisponibilidadeBadge;
